import type { IFirebaseUserRepository } from "@/core/repositories/interfaces/IFirebaseUserRepository";
import type {
  FirebaseUser,
  ListFirebaseUsersInput,
  ListFirebaseUsersResult,
  PreferenceStat,
  PreferenceStatsResult,
} from "@/core/services/types/firebase-user.types";
import { createClient } from "@/lib/supabase";

function isFirebaseUser(value: unknown): value is FirebaseUser {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === "string" && typeof row.email === "string";
}

function isPreferenceStat(value: unknown): value is PreferenceStat {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "string" &&
    typeof row.label === "string" &&
    typeof row.count === "number"
  );
}

export class FirebaseUserRepository implements IFirebaseUserRepository {
  async list(
    input: ListFirebaseUsersInput = {},
  ): Promise<ListFirebaseUsersResult> {
    const supabase = await createClient();
    const { data, error } = await supabase.functions.invoke(
      "list-firebase-users",
      {
        body: {
          q: input.q?.trim() || undefined,
          pageSize: input.pageSize,
          pageToken: input.pageToken ?? undefined,
        },
      },
    );

    if (error) {
      console.error("[FirebaseUserRepository.list]", error.message);
      return { users: [], nextPageToken: null, total: 0 };
    }

    const payload = data as {
      success?: boolean;
      error?: string;
      data?: {
        users?: unknown[];
        nextPageToken?: string | null;
        total?: unknown;
      };
    };

    if (!payload?.success || !payload.data) {
      console.error(
        "[FirebaseUserRepository.list]",
        payload?.error ?? "unknown error",
      );
      return { users: [], nextPageToken: null, total: 0 };
    }

    const users = (payload.data.users ?? []).filter(isFirebaseUser);
    const total =
      typeof payload.data.total === "number" && Number.isFinite(payload.data.total)
        ? Math.max(0, Math.trunc(payload.data.total))
        : 0;
    return {
      users,
      nextPageToken: payload.data.nextPageToken ?? null,
      total,
    };
  }

  async listPreferenceStats(): Promise<PreferenceStatsResult> {
    const supabase = await createClient();
    const { data, error } = await supabase.functions.invoke(
      "list-firebase-preference-stats",
      { body: {} },
    );

    if (error) {
      console.error("[FirebaseUserRepository.listPreferenceStats]", error.message);
      return { artForms: [], artisticPeriods: [] };
    }

    const payload = data as {
      success?: boolean;
      error?: string;
      data?: {
        artForms?: unknown[];
        artisticPeriods?: unknown[];
      };
    };

    if (!payload?.success || !payload.data) {
      console.error(
        "[FirebaseUserRepository.listPreferenceStats]",
        payload?.error ?? "unknown error",
      );
      return { artForms: [], artisticPeriods: [] };
    }

    return {
      artForms: (payload.data.artForms ?? []).filter(isPreferenceStat),
      artisticPeriods: (payload.data.artisticPeriods ?? []).filter(
        isPreferenceStat,
      ),
    };
  }
}
